import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { styled } from "@mui/styles";
import { Card, CardActionArea, Grid, Typography, Stack } from "@mui/material";
import { usePaidServices } from "@/hooks";
import { PaidServicesTooltip } from "@/components/PaidServicesTooltip";
import { useAccessAppComponent } from "@/hooks/useAccessAppComponent";
import CustomCardSending from "./CustomCardSending";
import Document from "./Document";
import usePoll from "../../utils/usePoll";
import {
  ALLOWED_DOCUMENT_TYPE,
  ERRORS_MESSAGE,
  MAX_DOCUMENT_UPLOAD_SIZE,
  SUCCESS_MESSAGE,
  VALID_DOCUMENT_TYPE_EXTENSION,
} from "../../constants";
import { RichTooltip } from "../CommonComponents/RichTooltip";
import {
  uploadDocument,
  saveValue,
  userDetailsSelector,
  startVerification,
  getIdentityVerification,
} from "../../store/slices/applicationFormSlice";
import { customerSelector } from "../../store/slices/customerSlice";
import { openSnackbar } from "../../store/slices/snackbarSlice";

const Input = styled("input")({
  display: "none",
});

const identityDocuments = [
  "Driver licence (back)",
  "Driver licence (front)",
  "Customer photo",
  "Verification certificate",
  "ID document",
];

const Identity = React.memo(({ customer, applicationId, isDisableEdit }) => {
  const fileInput = useRef();
  const { isCustomerLogin } = useSelector(customerSelector);
  const { uploadIDLoading = "", sendingIDLink, idVerificationRequest } = useSelector(userDetailsSelector);
  const { canUsePaidServices } = usePaidServices();
  const { accessComponents } = useAccessAppComponent();
  const {
    application: {
      customer: {
        isIdVerificationEnabled,
      },
    },
  } = accessComponents;

  const dispatch = useDispatch();
  const isDisable = isCustomerLogin ? false : isDisableEdit;
  const [error, setError] = useState("");

  const [delay, setDelay] = useState(null);

  const onStart = () => {
    setDelay(30000);
  };

  const handle = {
    startIdentityVerification: async (customerId, mobile) => {
      let res = await dispatch(startVerification({ data: { customerId, applicationId, mobile } })).unwrap();

      if (res) {
        dispatch(openSnackbar({
          message: SUCCESS_MESSAGE.idVerification,
          intent: "success",
        }));
        dispatch(saveValue({ sendingIDLink: false }));

        handle.getIdentityVerificationFn(customer?._id);
      } else {
        dispatch(openSnackbar({
          message: ERRORS_MESSAGE.fetchErrorMsg,
          intent: "danger",
        }));
        dispatch(saveValue({ sendingIDLink: false }));
      }
    },
    getIdentityVerificationFn: (customerId) => {
      onStart();
    },
    uploadID: (event) => {
      const files = event.target.files;
      const newSelectedFiles = [];
      const formData = new FormData();

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        if (ALLOWED_DOCUMENT_TYPE.includes(file.type)) {
          if (file.size <= MAX_DOCUMENT_UPLOAD_SIZE) {
            newSelectedFiles.push(file);
          } else {
            setError(ERRORS_MESSAGE.documentSizeMsg);
          }
        } else {
          setError(ERRORS_MESSAGE.documentTypeMsg);
        }
      }
      if (newSelectedFiles?.length) {
        dispatch(saveValue({ uploadIDLoading: true }));
        newSelectedFiles.forEach((file) => {
          formData.append("file", file);
        });
        // Update the formData object
        formData.append("applicationId", applicationId);
        formData.append("customerId", customer._id);
        formData.append("name", "ID document");

        dispatch(uploadDocument({ formData })).then((res) => {
          if (res?.payload?.data) {
            dispatch(openSnackbar({
              message: SUCCESS_MESSAGE.fileUploadSuccess,
              intent: "success",
            }));
          } else {
            dispatch(openSnackbar({
              message: ERRORS_MESSAGE.fetchErrorMsg,
              intent: "danger",
            }));
          }
        });
        event.target.input = null;
        setError("");
      }
    },
  };

  usePoll(async () => {
    dispatch(
      getIdentityVerification({
        data: {
          customer: customer._id,
        },
      }),
    );
  }, delay);

  useEffect(() => {
    if (customer?.documents?.length > 0) setDelay(null);
  }, [customer]);

  useEffect(() => {
    if (uploadIDLoading === false) {
      dispatch(saveValue({ uploadIDLoading: null }));
    }
  }, [uploadIDLoading]);

  useEffect(() => {
    if (sendingIDLink === false) {
      dispatch(saveValue({ sendingIDLink: null }));
    }
  }, [sendingIDLink]);

  return (
    <>
      <Grid
        container
        item
        xs={12}
        spacing={0}
        style={{
          margin: "0 0 30px",
          borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
          paddingBottom: "30px",
        }}
      >
        <Grid item xs={12}>
          <Typography fontWeight={600} style={{ marginBottom: "15px" }}>
            Identity
          </Typography>
        </Grid>

        <Grid item xs={12} sx={{ minWidth: 0, marginBottom: "20px" }}>
          <Stack spacing={1} direction="row" flexWrap="wrap" useFlexGap>
            {customer?.documents?.length > 0 &&
              customer?.documents
                ?.filter((doc) => identityDocuments.includes(doc.name))
                ?.map((doc, idx) => <Document key={doc?._id ?? idx} document={doc} customer={customer} name="identity" />)}
          </Stack>
        </Grid>

        <Grid
          container
          item
          spacing={2}
          direction="row"
        >
          {!isCustomerLogin && isIdVerificationEnabled && (
            <Grid item xs={12} sm={6}>
              <RichTooltip
                title={
                  canUsePaidServices ? null : <PaidServicesTooltip />
                }
              >
                <Card
                  onClick={() => {
                    if (
                      !canUsePaidServices ||
                        !customer?.phoneNumbers?.slice(-1)[0]?.number
                        ? true
                        : false
                    ) {
                      return;
                    }
                    handle.startIdentityVerification(customer._id, customer?.phoneNumbers?.slice(-1)[0]?.number);
                    dispatch(saveValue({ sendingIDLink: true }));
                  }}
                >
                  <CardActionArea disableRipple={!customer?.phoneNumbers?.slice(-1)[0]?.number ? true : false}>
                    <CustomCardSending
                      title={"Get ID documents"}
                      description={`Send a self service link to 
                      upload ID documents and verify identity.`}
                      imagePath={"/static/ocr-labs-logo-dark.png"}
                      loadingState={sendingIDLink}
                      financialState={false}
                      disabled={
                        isDisable ||
                          !canUsePaidServices ||
                          !customer?.phoneNumbers?.slice(-1)[0]?.number
                          ? true
                          : false
                      }
                      icon={false}
                      lastTitle={"Sending SMS link..."}
                    />
                  </CardActionArea>
                </Card>

                {!customer?.phoneNumbers?.filter((a) => a.status === "current")[0]?.number && (
                  <Typography variant="caption">Requires customer mobile phone number</Typography>
                )}
                {idVerificationRequest && <Typography variant="caption">{SUCCESS_MESSAGE.idVerification}</Typography>}
              </RichTooltip>
            </Grid>
          )}

          <Grid item xs={12} sm={6}>
            <Card style={{ height: "120px" }}>
              <Input
                accept={VALID_DOCUMENT_TYPE_EXTENSION}
                ref={fileInput}
                id={`upload-id-${customer._id}`}
                multiple
                type="file"
                value={null}
                onChange={(event) => handle.uploadID(event)}
              />
              <CardActionArea onClick={() => fileInput.current.click()}>
                <CustomCardSending
                  title={"Upload ID documents"}
                  description={`Upload ID documents from files stored locally on your
                    computer.`}
                  imagePath={""}
                  loadingState={uploadIDLoading}
                  financialState={false}
                  icon={false}
                  lastTitle={"Upload ID document..."}
                  disabled={isDisable}
                />
              </CardActionArea>
            </Card>
            {error && (
              <Typography variant="body2" style={{ color: "red" }}>
                {error}
              </Typography>
            )}
          </Grid>
        </Grid>
      </Grid>
    </>
  );
});

export default Identity;
